import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Organization } from '@/types/organization';

interface OrganizationSelectorProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelect: (organization: Organization) => void;
  disabled?: boolean;
}

export function OrganizationSelector({ 
  organizations, 
  selectedOrganization, 
  onSelect, 
  disabled 
}: OrganizationSelectorProps) {
  const [open, setOpen] = useState(false);
  const { language, t, dir } = useLanguage();

  if (organizations.length <= 1) {
    return null; // Don't show selector if only one or no organizations
  }

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-[300px] justify-between",
              dir === 'rtl' ? 'flex-row-reverse' : ''
            )}
            disabled={disabled}
          >
            <span className="truncate">
              {selectedOrganization 
                ? (selectedOrganization.id === 'general'
                   ? t('dashboard.instructor.dashboard.generalAudience')
                   : (language === 'ar' 
                      ? selectedOrganization.name 
                      : (selectedOrganization.nameEn || selectedOrganization.name)))
                : (language === 'ar' ? 'اختر المنظمة...' : 'Select organization...')
              }
            </span>
            <ChevronsUpDown className={`h-4 w-4 shrink-0 opacity-50 ${dir === 'rtl' ? 'mr-2' : 'ml-2'}`} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align={dir === 'rtl' ? 'end' : 'start'}>
          <Command>
            <CommandInput 
              placeholder={language === 'ar' ? 'البحث عن منظمة...' : 'Search organizations...'} 
              dir={dir}
            />
            <CommandList>
              <CommandEmpty>
                {language === 'ar' ? 'لم يتم العثور على منظمات.' : 'No organizations found.'}
              </CommandEmpty>
              <CommandGroup>
                {organizations.map((org) => (
                  <CommandItem
                    key={org.id}
                    value={org.id}
                    onSelect={() => {
                      onSelect(org);
                      setOpen(false);
                    }}
                    className={cn(
                      "flex items-center gap-2",
                      dir === 'rtl' ? 'flex-row-reverse' : ''
                    )}
                    dir={dir}
                  >
                    <Check
                      className={cn(
                        "h-4 w-4",
                        selectedOrganization?.id === org.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="font-medium">
                      {org.id === 'general'
                        ? t('dashboard.instructor.dashboard.generalAudience')
                        : (language === 'ar' 
                           ? org.name 
                           : (org.nameEn || org.name))
                      }
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}